import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import { apiUrl } from '../config';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TokenInfo {
  recipientName: string;
  recipientEmail: string;
  workOrderTitle: string;
  isExpired: boolean;
  isUsed: boolean;
  informeFileName?: string;
  checklistFileName?: string;
  informeDownloadUrl?: string;
  checklistDownloadUrl?: string;
}

interface PageState {
  loading: boolean;
  tokenInfo: TokenInfo | null;
  tokenError: 'invalid' | 'expired' | 'used' | null;
  step: 0 | 1 | 2;
  otpCode: string;
  otpError: string | null;
  otpLoading: boolean;
  signerName: string;
  signLoading: boolean;
  signError: string | null;
}

// ---------------------------------------------------------------------------
// Helper — public fetch (no auth header)
// ---------------------------------------------------------------------------

async function publicFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${apiUrl}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers ?? {})
    }
  });

  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message =
      (body as any)?.message ?? `Error ${res.status}: ${res.statusText}`;
    throw new Error(message);
  }

  return body as T;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TopBar() {
  return (
    <Box
      sx={{
        bgcolor: '#1a1a2e',
        color: '#fff',
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}
    >
      <Box
        component="img"
        src="/ferrari-logo.png"
        alt="Ferrari Crane"
        sx={{
          height: 32,
          width: 'auto',
          flexShrink: 0
        }}
      />
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, letterSpacing: '0.04em', color: '#fff' }}
      >
        Ferrari Crane
      </Typography>
      <Typography
        variant="caption"
        sx={{
          ml: 'auto',
          color: 'rgba(255,255,255,0.55)',
          display: { xs: 'none', sm: 'block' }
        }}
      >
        Portal de firma digital
      </Typography>
    </Box>
  );
}

function LoadingOverlay() {
  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function TokenErrorCard({ type }: { type: 'invalid' | 'expired' | 'used' }) {
  const messages: Record<string, { title: string; detail: string }> = {
    invalid: {
      title: 'Enlace de firma inválido',
      detail:
        'El enlace que utilizó no es válido. Por favor verifique que copió el enlace completo desde su correo.'
    },
    expired: {
      title: 'Enlace de firma expirado',
      detail:
        'Este enlace de firma ya expiró. Contacte al técnico responsable para que le envíe un nuevo enlace.'
    },
    used: {
      title: 'Este enlace ya fue utilizado',
      detail:
        'Su firma ya fue registrada anteriormente a través de este enlace. No es posible firmar dos veces.'
    }
  };

  const { title, detail } = messages[type];

  return (
    <Card elevation={3} sx={{ mt: 4 }}>
      <CardContent sx={{ textAlign: 'center', py: 5 }}>
        <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
          {detail}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

interface PublicSigningPageProps {
  token: string;
}

export default function PublicSigningPage({ token }: PublicSigningPageProps) {
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  const [state, setState] = useState<PageState>({
    loading: true,
    tokenInfo: null,
    tokenError: null,
    step: 0,
    otpCode: '',
    otpError: null,
    otpLoading: false,
    signerName: '',
    signLoading: false,
    signError: null
  });

  const patch = useCallback(
    (partial: Partial<PageState>) =>
      setState((prev) => ({ ...prev, ...partial })),
    []
  );

  // --- Load token info on mount ---
  useEffect(() => {
    if (!token) {
      patch({ loading: false, tokenError: 'invalid' });
      return;
    }

    publicFetch<TokenInfo>(`signatures/public/token-info/${token}`)
      .then((info) => {
        if (info.isUsed) {
          patch({ loading: false, tokenError: 'used' });
        } else if (info.isExpired) {
          patch({ loading: false, tokenError: 'expired' });
        } else {
          patch({
            loading: false,
            tokenInfo: info,
            signerName: info.recipientName ?? ''
          });
        }
      })
      .catch(() => {
        patch({ loading: false, tokenError: 'invalid' });
      });
  }, [token]);

  // --- OTP verification ---
  const handleVerifyOtp = async () => {
    if (state.otpCode.trim().length !== 6) {
      patch({ otpError: 'Por favor ingrese el código de 6 dígitos.' });
      return;
    }

    patch({ otpLoading: true, otpError: null });

    try {
      await publicFetch('signatures/public/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ token, otpCode: state.otpCode.trim() })
      });
      patch({ otpLoading: false, step: 1 });
    } catch (err: any) {
      const msg = err.message ?? '';
      let friendly = 'Código incorrecto. Verifique el código e intente de nuevo.';

      if (msg.toLowerCase().includes('attempt') || msg.toLowerCase().includes('intento')) {
        friendly = 'Se excedió el número máximo de intentos. Solicite un nuevo enlace.';
      } else if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('expir')) {
        friendly = 'El código ha expirado. Solicite un nuevo enlace al técnico.';
      }

      patch({ otpLoading: false, otpError: friendly });
    }
  };

  // --- Submit signature ---
  const handleSign = async () => {
    if (!sigCanvasRef.current) {
      patch({ signError: 'Por favor dibuje su firma antes de continuar.' });
      return;
    }

    if (!state.signerName.trim()) {
      patch({ signError: 'Por favor confirme su nombre.' });
      return;
    }

    patch({ signLoading: true, signError: null });

    let imageBase64: string;
    try {
      imageBase64 = sigCanvasRef.current.toDataURL('image/png');
    } catch (e) {
      console.error('Signature capture error:', e);
      patch({ signLoading: false, signError: 'Error al capturar la firma. Intente de nuevo.' });
      return;
    }

    try {
      await publicFetch(`signatures/public/sign/${token}`, {
        method: 'POST',
        body: JSON.stringify({
          signatureBase64: imageBase64,
          signerName: state.signerName.trim()
        })
      });
      patch({ signLoading: false, step: 2 });
    } catch (err: any) {
      const msg = err.message ?? '';
      let friendly = 'No fue posible registrar su firma. Intente de nuevo.';

      if (msg.toLowerCase().includes('otp') || msg.toLowerCase().includes('verificac')) {
        friendly = 'Verificación OTP requerida. Por favor recargue la página.';
      } else if (msg.toLowerCase().includes('used') || msg.toLowerCase().includes('utilizado')) {
        friendly = 'Este enlace ya fue utilizado anteriormente.';
      }

      patch({ signLoading: false, signError: friendly });
    }
  };

  const handleClearSignature = () => {
    sigCanvasRef.current?.clear();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (state.loading) {
    return (
      <>
        <TopBar />
        <LoadingOverlay />
      </>
    );
  }

  if (state.tokenError) {
    return (
      <>
        <TopBar />
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <TokenErrorCard type={state.tokenError} />
        </Container>
      </>
    );
  }

  const { tokenInfo } = state;
  const steps = ['Verificación', 'Firma', 'Confirmación'];

  return (
    <>
      <TopBar />

      <Container
        maxWidth="sm"
        sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}
      >
        {/* Work order context */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
          >
            Firma de informe técnico
          </Typography>
          {tokenInfo?.workOrderTitle && (
            <Typography variant="body2" color="text.secondary">
              Orden de trabajo: <strong>{tokenInfo.workOrderTitle}</strong>
            </Typography>
          )}
        </Box>

        {/* Stepper */}
        <Stepper
          activeStep={state.step}
          sx={{ mb: 3, '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* --- Step 0: OTP Verification --- */}
        {state.step === 0 && (
          <>
            {/* Documents to be signed */}
            {(tokenInfo?.informeFileName || tokenInfo?.checklistFileName) && (
              <Card elevation={2} sx={{ mb: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Documentos a firmar
                  </Typography>
                  {tokenInfo?.informeFileName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2">
                        Informe Técnico: <strong>{tokenInfo.informeFileName}</strong>
                      </Typography>
                      {tokenInfo.informeDownloadUrl && (
                        <a
                          href={`${apiUrl}signatures/public/document/${token}/informe`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.8rem', marginLeft: 4 }}
                        >
                          Ver documento
                        </a>
                      )}
                    </Box>
                  )}
                  {tokenInfo?.checklistFileName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2">
                        Checklist: <strong>{tokenInfo.checklistFileName}</strong>
                      </Typography>
                      {tokenInfo.checklistDownloadUrl && (
                        <a
                          href={`${apiUrl}signatures/public/document/${token}/checklist`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.8rem', marginLeft: 4 }}
                        >
                          Ver documento
                        </a>
                      )}
                    </Box>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    Al firmar, usted confirma haber revisado y estar de acuerdo con el contenido de ambos documentos.
                  </Typography>
                </CardContent>
              </Card>
            )}

          <Card elevation={3}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Verificación de código
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Hola{tokenInfo?.recipientName ? `, ${tokenInfo.recipientName}` : ''}. Ingrese el
                código de 6 dígitos que fue enviado a su correo electrónico para verificar su
                identidad.
              </Typography>

              <TextField
                label="Código de verificación"
                placeholder="123456"
                value={state.otpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  patch({ otpCode: val, otpError: null });
                }}
                fullWidth
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 6,
                  style: { fontSize: '1.5rem', letterSpacing: '0.4em', textAlign: 'center' }
                }}
                error={!!state.otpError}
                helperText={state.otpError ?? ' '}
                disabled={state.otpLoading}
                sx={{ mb: 2 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleVerifyOtp();
                }}
              />

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleVerifyOtp}
                disabled={state.otpLoading || state.otpCode.length !== 6}
                sx={{ py: 1.5, fontWeight: 700 }}
              >
                {state.otpLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Verificar código'
                )}
              </Button>
            </CardContent>
          </Card>
          </>
        )}

        {/* --- Step 1: Signature --- */}
        {state.step === 1 && (
          <Card elevation={3}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Firmar documento
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Dibuje su firma en el recuadro a continuación y confirme su nombre.
              </Typography>

              {/* Signer name */}
              <TextField
                label="Nombre del firmante"
                value={state.signerName}
                onChange={(e) => patch({ signerName: e.target.value, signError: null })}
                fullWidth
                disabled={state.signLoading}
                sx={{ mb: 2.5 }}
              />

              <Divider sx={{ mb: 2 }} />

              {/* Signature canvas */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Dibuje su firma en el recuadro
              </Typography>

              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: '#fafafa',
                  touchAction: 'none',
                  mb: 1
                }}
              >
                <SignatureCanvas
                  ref={sigCanvasRef}
                  penColor="#1a1a2e"
                  canvasProps={{
                    style: { width: '100%', height: 180, display: 'block' }
                  }}
                />
              </Box>

              <Button
                variant="text"
                size="small"
                onClick={handleClearSignature}
                disabled={state.signLoading}
                sx={{ mb: 2.5, color: 'text.secondary' }}
              >
                Limpiar firma
              </Button>

              {state.signError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {state.signError}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSign}
                disabled={state.signLoading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  bgcolor: '#1a1a2e',
                  '&:hover': { bgcolor: '#2d2d4e' }
                }}
              >
                {state.signLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Firmar documento'
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* --- Step 2: Success --- */}
        {state.step === 2 && (
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center', py: 6, px: { xs: 2.5, sm: 4 } }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 72, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Firma registrada exitosamente
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Su firma ha sido registrada. Puede cerrar esta página.
              </Typography>
              {tokenInfo?.recipientEmail && (
                <Typography variant="body2" color="text.secondary">
                  Se enviará una confirmación a{' '}
                  <strong>{tokenInfo.recipientEmail}</strong>.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.disabled"
          align="center"
          sx={{ display: 'block', mt: 4 }}
        >
          Ferrari Crane — Portal de firma digital seguro
        </Typography>
      </Container>
    </>
  );
}
